/**
 * Warmup game intros (first-run how-to-play) — client constants.
 *
 * The first time a player opens a Warmup game we show a per-mode how-to-play bottom-sheet, modeled on
 * the handoff design (`Warmup Soccer Wordle.dc.html` → "HOW-TO-PLAY INTRO"). Behaviour is first-time-only
 * per mode, persisted device-locally (no account scoping) — see specs/warmup-game-intros/spec.md.
 *
 * Covered modes: Wordle, Higher or Lower, Footballer Quiz, Keeper Duel. Guess the Value is
 * intentionally deferred (add a mode here + a body to ship it).
 */

import { WORDLE_GAME_KEY } from './warmup.constants';
import { HILO_GAME_KEY } from './higherLower.constants';
import { FOOTBALLER_GAME_KEY, FOOTBALLER_MAX_GUESSES } from './footballerQuiz.constants';
import { KEEPER_DUEL_GAME_KEY, KEEPER_DUEL_ACCENT } from './keeperDuel.constants';

/** Modes that have a first-run intro, in no particular order. */
export const WARMUP_INTRO_MODES = ['wordle', 'hilo', 'footballer', 'keeperduel'] as const;

export type WarmupIntroMode = (typeof WARMUP_INTRO_MODES)[number];

/** Device-local storage key prefix; one flag per mode (`<prefix><mode>`). Bump the v-suffix to re-show. */
export const WARMUP_INTRO_STORAGE_PREFIX = 'warmup_intro_seen_v1_';

/** Mode → server game_key, for analytics (`GAME_INTRO_SHOWN`). */
export const WARMUP_INTRO_GAME_KEY: Record<WarmupIntroMode, string> = {
  wordle: WORDLE_GAME_KEY,
  hilo: HILO_GAME_KEY,
  footballer: FOOTBALLER_GAME_KEY,
  keeperduel: KEEPER_DUEL_GAME_KEY,
};

export const WARMUP_INTRO_TEST_IDS = {
  sheet: 'warmup-intro-sheet',
  backdrop: 'warmup-intro-backdrop',
  skipHint: 'warmup-intro-skip-hint',
  playButton: 'warmup-intro-play',
  wordleBody: 'warmup-intro-body-wordle',
  hiloBody: 'warmup-intro-body-hilo',
  footballerBody: 'warmup-intro-body-footballer',
  keeperDuelBody: 'warmup-intro-body-keeperduel',
} as const;

/** Header copy + accent per mode (eyebrow colour matches each game's hub accent). */
export const WARMUP_INTRO_CONTENT: Record<
  WarmupIntroMode,
  { eyebrow: string; eyebrowColor: string; title: string; footer: string; footerGlyph: string }
> = {
  wordle: {
    eyebrow: 'Soccer Wordle',
    eyebrowColor: '#A78BFA',
    title: 'Guess the footballer',
    footer: '6 guesses · new footballer daily',
    footerGlyph: '📅',
  },
  hilo: {
    eyebrow: 'Higher or Lower',
    eyebrowColor: '#6EE7B7',
    title: 'More or fewer goals?',
    footer: 'Call it right to build your streak',
    footerGlyph: '🔥',
  },
  footballer: {
    eyebrow: 'Footballer Quiz',
    eyebrowColor: '#A78BFA',
    title: 'Guess the mystery footballer',
    footer: `${FOOTBALLER_MAX_GUESSES} guesses · new player daily`,
    footerGlyph: '📅',
  },
  keeperduel: {
    eyebrow: 'Keeper Duel',
    eyebrowColor: KEEPER_DUEL_ACCENT,
    title: 'Beat the keeper',
    footer: 'One miss ends the run · new hazard every 3 goals',
    footerGlyph: '🧤',
  },
};
